/**
 * Account-link plumbing shared by the QR card, the Settings › Account Sync
 * panel, and the /link landing page.
 *
 * The link code itself comes from the API (POST /api/extension/account-link/
 * code — single use, ten minutes). Everything here is presentation and
 * local-state: how the code is shown, what URL the QR encodes, when the app
 * scheme is worth attempting, and whether the one-time post-sign-in prompt
 * has already been shown for an account.
 */

/** Where a scanned QR lands. Absolute, because a phone opens it, not this tab. */
export const PHONE_LINK_BASE_URL = 'https://app.itsbaba.com/link'

/** Custom scheme the installed app registers; mobile handles `baba://link`. */
export const PHONE_LINK_SCHEME = 'baba://link'

/**
 * Builds already in the field only register the older `babahebrew` scheme;
 * `baba` ships with the next store build. The /link page attempts this one
 * second, so both generations of the app open from the same QR.
 */
export const PHONE_LINK_LEGACY_SCHEME = 'babahebrew://link'

/** How long we wait for the app-scheme attempt before showing store badges. */
export const APP_SCHEME_FALLBACK_MS = 1500

/** Session marker: a sign-in just happened and the prompt has not run yet. */
const PROMPT_PENDING_KEY = 'baba.phone_link.pending'

/** Durable marker: accounts that have already been shown the prompt once. */
const PROMPT_SEEN_KEY = 'baba.phone_link.seen.v1'

/** Cap the remembered list so the key cannot grow without bound. */
const MAX_REMEMBERED_ACCOUNTS = 20

/**
 * Where a code came from. `join` is an organization's join page: the apps
 * redeem those at once, even during first-run setup, instead of asking the
 * member to confirm a sheet (LinkCodeDeepLink.parseJoin / parseJoinLinkCode).
 */
export type PhoneLinkSource = 'join'

function linkQuery(code: string, from?: PhoneLinkSource): string {
  return `?code=${encodeURIComponent(code)}${from ? `&from=${from}` : ''}`
}

/** The URL a scanned QR opens. */
export function phoneLinkUrl(code: string, from?: PhoneLinkSource): string {
  return `${PHONE_LINK_BASE_URL}${linkQuery(code, from)}`
}

/** The deep link the /link page tries before falling back to the stores. */
export function phoneLinkSchemeUrl(code: string, from?: PhoneLinkSource): string {
  return `${PHONE_LINK_SCHEME}${linkQuery(code, from)}`
}

export function phoneLinkLegacySchemeUrl(code: string, from?: PhoneLinkSource): string {
  return `${PHONE_LINK_LEGACY_SCHEME}${linkQuery(code, from)}`
}

/** `from` read back off the /link page's own URL; only known values pass. */
export function readPhoneLinkSource(raw: string | null): PhoneLinkSource | undefined {
  return raw === 'join' ? 'join' : undefined
}

/**
 * Codes are handled by eye and by thumb, so they are grouped in fours. An
 * eight-character code reads as ABCD-EFGH; anything else is left alone rather
 * than being chopped at a place that would mislead.
 */
export function formatLinkCode(code: string): string {
  const clean = code.trim().toUpperCase()
  return clean.length === 8 ? `${clean.slice(0, 4)}-${clean.slice(4)}` : clean
}

/** Strips the grouping dash and any stray input back to the bare code. */
export function normalizeLinkCode(raw: string): string {
  return raw.replace(/[^0-9a-zA-Z]/g, '').toUpperCase().slice(0, 8)
}

/** Whole seconds left before `expiresAt`, floored at zero. */
export function secondsUntilExpiry(expiresAt: string, now: number = Date.now()): number {
  const expiry = new Date(expiresAt).getTime()
  if (!Number.isFinite(expiry)) return 0
  return Math.max(0, Math.floor((expiry - now) / 1000))
}

/** `M:SS`, the shape people read a countdown in. */
export function formatCountdown(totalSeconds: number): string {
  const clamped = Math.max(0, Math.floor(totalSeconds))
  const minutes = Math.floor(clamped / 60)
  const seconds = clamped % 60
  return `${minutes}:${String(seconds).padStart(2, '0')}`
}

// ───────────────────── one-time post-sign-in prompt state ─────────────────────

/**
 * Whether the "Use baba on Your Phone" panel should appear.
 *
 * Kept pure so the rule is testable on its own: a sign-in must have happened
 * in this session, the account must not have seen the panel before, and an
 * account already linked to a phone has nothing to gain from it. `linked` is
 * `null` while the status request is still in flight — the panel waits rather
 * than flashing in front of someone who is already linked.
 */
export function shouldShowPhoneLinkPrompt(input: {
  pendingEmail: string | null
  seenEmails: readonly string[]
  linked: boolean | null
}): boolean {
  const { pendingEmail, seenEmails, linked } = input
  if (!pendingEmail) return false
  if (linked !== false) return false
  return !seenEmails.includes(pendingEmail.trim().toLowerCase())
}

/** Adds `email` to the remembered list, newest last, de-duplicated and capped. */
export function withSeenAccount(seenEmails: readonly string[], email: string): string[] {
  const normalized = email.trim().toLowerCase()
  if (!normalized) return [...seenEmails]
  const without = seenEmails.filter((entry) => entry !== normalized)
  return [...without, normalized].slice(-MAX_REMEMBERED_ACCOUNTS)
}

function readSession(key: string): string | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage.getItem(key)
  } catch {
    return null
  }
}

/**
 * Records that a sign-in just succeeded. Sign-in reloads the page, so an
 * in-memory callback would not survive to the other side — the marker is what
 * carries the intent across the reload.
 */
export function markPhoneLinkPromptPending(email: string): void {
  try {
    if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(PROMPT_PENDING_KEY, email.trim().toLowerCase())
  } catch {
    // Best effort — worst case the panel simply does not appear.
  }
}

export function readPhoneLinkPromptPending(): string | null {
  return readSession(PROMPT_PENDING_KEY)
}

export function clearPhoneLinkPromptPending(): void {
  try {
    if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem(PROMPT_PENDING_KEY)
  } catch {
    // Ignored — see markPhoneLinkPromptPending.
  }
}

export function readSeenPhoneLinkAccounts(): string[] {
  try {
    if (typeof localStorage === 'undefined') return []
    const raw = localStorage.getItem(PROMPT_SEEN_KEY)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter((entry): entry is string => typeof entry === 'string') : []
  } catch {
    return []
  }
}

/** Marks the panel as shown for `email` and clears the pending marker. */
export function markPhoneLinkPromptSeen(email: string): void {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(PROMPT_SEEN_KEY, JSON.stringify(withSeenAccount(readSeenPhoneLinkAccounts(), email)))
    }
  } catch {
    // Ignored — see markPhoneLinkPromptPending.
  }
  clearPhoneLinkPromptPending()
}
